import {Box, CardContent, Chip, Divider, Stack, Tooltip, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import Card from 'components/Display/Cards/Card'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useCurrentRateQuery} from 'store/Store'

const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]
const HOURS_IN_DAY = 24

const buildSet = (values = []) => new Set(Array.isArray(values) ? values : [])

const buildTooltipLabel = (values = [], formatter) => {
    if (!values?.length) return 'N/A'
    const unique = [...new Set(values)].sort((a, b) => a - b)
    return unique.map(formatter).join(', ')
}

const renderMonthsGrid = (theme, months = [], t) => {
    if (!months?.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('general.notAvailable', 'N/A')}
            </Typography>
        )
    }

    const active = buildSet(months)
    const tooltipLabel = buildTooltipLabel(months, (month) => MONTH_NAMES[month - 1] || month)
    const primary = theme.palette.secondary.main
    const inactive = theme.palette.mode === 'dark' ? alpha('#e2e8f0', 0.16) : alpha('#475569', 0.1)

    return (
        <Tooltip arrow title={tooltipLabel}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(6, minmax(32px, 1fr))',
                    },
                    gap: {xs: 0.4},
                }}>
                {MONTH_NAMES.map((label, index) => {
                    const isActive = active.has(index + 1)
                    return (
                        <Box
                            key={`month_${label}`}
                            sx={{
                                borderRadius: 1.5,
                                px: {xs: 0.35},
                                py: {xs: 0.6},
                                textAlign: 'center',
                                fontSize: {xs: '0.68rem'},
                                fontWeight: 600,
                                backgroundColor: isActive ? alpha(primary, 0.2) : inactive,
                                color: isActive ? primary : theme.palette.text.secondary,
                            }}>
                            {t(`months.short.${label.toLowerCase()}`, label)}
                        </Box>
                    )
                })}
            </Box>
        </Tooltip>
    )
}

const renderHoursGrid = (theme, hours = [], t) => {
    if (!hours?.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t('general.notAvailable', 'N/A')}
            </Typography>
        )
    }

    const active = buildSet(hours)
    const tooltipLabel = buildTooltipLabel(hours, (hour) => `${String(hour).padStart(2, '0')}:00`)
    const primary = theme.palette.primary.main
    const inactive = theme.palette.mode === 'dark' ? alpha('#e2e8f0', 0.16) : alpha('#475569', 0.1)

    return (
        <Tooltip arrow title={tooltipLabel}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(8, minmax(28px, 1fr))',
                    },
                    gap: {xs: 0.4},
                }}>
                {Array.from({length: HOURS_IN_DAY}, (_, index) => {
                    const isActive = active.has(index)
                    return (
                        <Box
                            key={`hour_${index}`}
                            sx={{
                                borderRadius: 1.5,
                                px: {xs: 0.35},
                                py: {xs: 0.6},
                                textAlign: 'center',
                                fontSize: {xs: '0.68rem'},
                                fontWeight: 600,
                                backgroundColor: isActive ? alpha(primary, 0.22) : inactive,
                                color: isActive ? primary : theme.palette.text.secondary,
                            }}>
                            {String(index).padStart(2, '0')}
                        </Box>
                    )
                })}
            </Box>
        </Tooltip>
    )
}

const CurrentRate = () => {
    const {data: currentRate} = useCurrentRateQuery()
    const {t} = useTranslation()
    const theme = useTheme()

    const renderedRates = useMemo(() => {
        if (!currentRate?.length) return null

        return currentRate.map((rate) => {
            const monthsView = renderMonthsGrid(theme, rate.months, t)
            const hoursView = renderHoursGrid(theme, rate.hours, t)

            return (
                <Box
                    key={rate._id || `${rate.name}-${rate.typeDay}`}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        p: 2.5,
                    }}>
                    <Stack direction="column" spacing={1.25}>
                        <Typography variant="h4" fontWeight={600} sx={{minWidth: 80}}>
                            {rate.rate?.toFixed(3) || rate.rate} ₪
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {rate.name && (
                                <Chip
                                    label={t(`selectOptions.${rate.name}`)}
                                    color="primary"
                                    size="small"
                                />
                            )}
                            {rate.typeDay && (
                                <Chip
                                    label={t(`selectOptions.${rate.typeDay}`)}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {rate.category && (
                                <Chip
                                    label={t(`selectOptions.${rate.category}`)}
                                    color="secondary"
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {rate.isGlobal && (
                                <Chip
                                    label={t('rates.global_label', 'Global rate')}
                                    variant="outlined"
                                    color="default"
                                    size="small"
                                />
                            )}
                        </Stack>
                    </Stack>

                    <Divider
                        flexItem
                        sx={{my: 1.5, borderColor: alpha(theme.palette.divider, 0.4)}}
                    />

                    <Stack spacing={1.5} direction={{sx: 'row', sm: 'column'}}>
                        <Stack spacing={0.5}>
                            <Typography variant="overline" color="text.secondary">
                                {t('rates.months', 'Months')}
                            </Typography>
                            {monthsView}
                        </Stack>
                        <Stack spacing={0.5}>
                            <Typography variant="overline" color="text.secondary">
                                {t('rates.hours', 'Hours')}
                            </Typography>
                            {hoursView}
                        </Stack>
                    </Stack>
                </Box>
            )
        })
    }, [currentRate, theme, t])

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 2, p: 3}}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={600} color="text.primary">
                        {t('dashboard.currentRate')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'dashboard.current_rate_subtitle',
                            'Active tariffs applied to your account.'
                        )}
                    </Typography>
                </Stack>

                {renderedRates?.length ? (
                    <Stack spacing={2} mt={1}>
                        {renderedRates}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                        {t('dashboard.noRateAvailable')}
                    </Typography>
                )}
            </CardContent>
        </Card>
    )
}

export default CurrentRate
